import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useCompany } from './CompanyContext';
import { useAuth } from './AuthContext';
import { settingsAPI, addOnsAPI } from '../services/apiService';
import { configureDateLocale } from '../utils/dateHelpers';
import soundService from '../services/soundService';

const SettingsContext = createContext(null);

/**
 * Default settings used as fallbacks before the server responds.
 * These match the seed values in the CompanySettings migration.
 */
const DEFAULT_SETTINGS = {
    // Map
    mapCenterLat: 40.0960,
    mapCenterLng: -74.2177,
    mapDefaultZoom: 13,
    autocompleteCountries: '["us","ca"]',
    // Pricing
    minimumFare: 70,
    minStopCost: 10,
    stopDriverComp: 7,
    stopCompanySharePercent: 0.30,
    stopPerMileRate: 3,
    formulaCompanySharePercent: 0.30,
    roundTripMultiplier: 1.5,
    defaultPerMileRate: 2.00,
    defaultMinPerHourRate: 30,
    // Wait Time
    freeWaitPickupMinutes: 5,
    freeWaitStopMinutes: 3,
    waitTimeRateStandard: 0.50,
    waitTimeRatePremium: 1.00,
    premiumWaitTimeCarTypes: '[4,5,6]',
    driverWaitTimeSharePercent: 0.85,
    // Scheduling
    rideVisibilityDays: 2,
    rideReminderMinutes: 30,
    upcomingRideThresholdMinutes: 45,
    interRideBufferMinutes: 30,
    scheduledDistributionCron: '0 7 * * *',
    reminderCheckCron: '*/5 * * * *',
    maxStops: 10,
    heartbeatTimeoutMinutes: 15,
    // Airport Detection
    airportDetectionRules: '[{"keywords":["jfk","john f. kennedy","kennedy airport"],"zip":"11430"},{"keywords":["lga","laguardia"],"zip":"11371"},{"keywords":["ewr","newark airport","newark liberty"],"zip":"07114"}]',
    // Timezone / Locale
    timezone: 'Eastern Standard Time',
    locale: 'en-US',
    use24HourTime: false,
    // Display / UI
    defaultPageSize: 50,
    dashboardCardColors: '{"futureRides":"#607d8b","driversOnJob":"#9c27b0","unsettledDrivers":"#ff9800","offlineDrivers":"#757575"}',
    mapMarkerColors: '{"driver":{"driving":{"fill":"#b10606","stroke":"#ffffff"},"drivingOffline":{"fill":"#FFD700","stroke":"#ffffff"},"available":{"fill":"#0a9702","stroke":"#ffffff"},"upcomingRide":{"fill":"#FF9800","stroke":"#FF5722"},"highlighted":{"fill":"#00BCD4","stroke":"#E91E63"}},"ride":{"missed":{"fill":"#f44336","stroke":"#b71c1c"},"inProgress":{"fill":"#ffeb3b","stroke":"#f57f17"},"future":{"fill":"#4caf50","stroke":"#1b5e20"},"completed":{"fill":"#9e9e9e","stroke":"#616161"},"atPickup":{"fill":"#2196f3","stroke":"#0d47a1"},"droppedOff":{"fill":"#9c27b0","stroke":"#4a148c"}}}',
    rideStatusColors: '{"canceled":"error","open":"primary","assigned":"warning","arriving":"warning","scheduled":"warning","atPickup":"info","inProgress":"default","paymentPending":"secondary","droppedOff":"secondary","completed":"success"}',
    driverStatusColors: '{"No Driver Found":"error","Inactive":"error","Available":"success","En-Route":"warning","Driving":"default"}',
    soundSettings: '{"types":{"message":{"frequency":800,"duration":0.15},"urgent":{"frequency":1200,"duration":0.25},"sent":{"frequency":600,"duration":0.1},"success":{"frequency":900,"duration":0.2},"broadcast":{"frequency":700,"duration":0.3}},"gain":{"urgent":0.4,"normal":0.3},"urgentKeywords":["cancel","reassign","remove","urgent","emergency","help"]}',
    // Feature Visibility
    showReportsTab: false,
    showRecurringCallsTab: true,
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addOns, setAddOns] = useState([]);

    const fetchAddOns = useCallback(async () => {
        try {
            const data = await addOnsAPI.getActive();
            setAddOns(data || []);
        } catch (err) {
            console.error('Failed to load add-ons:', err);
        }
    }, []);

    // Wait for company resolution and authentication before making any API calls
    const { isResolved, companyCode } = useCompany();
    const { isAuthenticated } = useAuth();

    const fetchSettings = useCallback(async () => {
        try {
            setLoading(true);
            const data = await settingsAPI.get();
            setSettings(data);
            configureDateLocale(data);
            soundService.configure(data);
            setError(null);
        } catch (err) {
            console.error('Failed to load company settings, using defaults:', err);
            setError(err);
            // Keep DEFAULT_SETTINGS as fallback
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch settings only once the company is resolved AND user is authenticated
    useEffect(() => {
        if (isResolved && isAuthenticated) {
            fetchSettings();
            fetchAddOns();
        } else {
            // Not ready yet — don't make authenticated API calls
            setLoading(false);
        }
    }, [isResolved, companyCode, isAuthenticated, fetchSettings, fetchAddOns]);

    /**
     * Update settings on server and refresh local state.
     * @param {object} newSettings - Full settings object to save
     */
    const updateSettings = useCallback(async (newSettings) => {
        const data = await settingsAPI.update(newSettings);
        setSettings(data);
        configureDateLocale(data);
        soundService.configure(data);
        return data;
    }, []);

    // ── Parsed helpers (JSON fields) ─────────────────────────────────
    const getAutocompleteCountries = useCallback(() => {
        try { return JSON.parse(settings.autocompleteCountries); }
        catch { return ['us', 'ca']; }
    }, [settings.autocompleteCountries]);

    const getPremiumWaitTimeCarTypes = useCallback(() => {
        try { return JSON.parse(settings.premiumWaitTimeCarTypes); }
        catch { return [3, 4, 5]; }
    }, [settings.premiumWaitTimeCarTypes]);

    const getAirportDetectionRules = useCallback(() => {
        try { return JSON.parse(settings.airportDetectionRules); }
        catch { return []; }
    }, [settings.airportDetectionRules]);

    const value = {
        settings,
        loading,
        error,
        fetchSettings,
        updateSettings,
        addOns,
        refreshAddOns: fetchAddOns,
        // Parsed helpers
        getAutocompleteCountries,
        getPremiumWaitTimeCarTypes,
        getAirportDetectionRules,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};

export default SettingsContext;
